// /app/frontend/src/pages/seo/AfricanDiasporaDatingPage.jsx
// Identity / community page for the global Black diaspora.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function AfricanDiasporaDatingPage() {
  const path = '/african-diaspora-dating';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'African Diaspora Dating', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="African Diaspora Dating & Community Platform"
        description="Connect with people across the African diaspora through meaningful relationships, friendship, family-building, and community."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="Across The Diaspora"
        headline="Dating Across The African Diaspora"
        sub="One global Black community — many cultures, traditions, and histories. UnityLove connects African Americans, Africans, Afro-Latinos, Afro-Caribbean people, and the wider diaspora."
        body="Find people who share your cultural fluency, family vision, and intentional approach to relationships — wherever in the diaspora your roots and your life lead."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Browse All Guides', to: '/guides' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            One Diaspora, Many Cultures
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              The African diaspora is the largest displaced and dispersed community on earth. From the U.S. South to São Paulo, from Lagos to London, from Kingston to Atlanta — Black identity carries different languages, different cuisines, different family structures, and different histories. What unites it is a shared cultural fluency that most dating apps simply don’t understand.
            </p>
            <p>
              UnityLove was built for people who don’t want to explain that fluency every time they swipe. Bring your full identity — wherever in the diaspora your story begins — and find people who recognize it.
            </p>
          </div>
        </section>

        {/* Who finds connection here */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            Who Finds Connection Here
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🇺🇸"
              accent="rose"
              title="African Americans"
              body="Singles, couples, and families building intentional connections rooted in shared cultural memory and present-day vision."
            />
            <FeatureCard
              icon="🌍"
              accent="emerald"
              title="Africans"
              body="People from Nigeria, Ghana, Kenya, South Africa, Ethiopia, and across the continent — connecting across diaspora lines and at home."
            />
            <FeatureCard
              icon="🌴"
              accent="amber"
              title="Caribbean Communities"
              body="Jamaican, Haitian, Trinidadian, Bahamian, Dominican, and broader Afro-Caribbean members — bringing island roots into modern connection."
            />
            <FeatureCard
              icon="💃🏾"
              accent="violet"
              title="Afro-Latinos"
              body="Brazilian, Colombian, Cuban, Puerto Rican, Dominican, and wider Afro-Latinx members — bilingual, multicultural, and proudly both."
            />
          </div>
        </section>

        {/* Shared cultural experiences */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Shared Cultural Experiences
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Across the diaspora, certain things echo. The role of food in family. The way music carries memory. The weight of how Black people are read and treated in any given country’s social structure. The strength of intergenerational households. The complicated, beautiful relationship many of us have with faith. The way humor and grief sit close to each other.
            </p>
            <p>
              Dating someone who shares these reference points isn’t required — but for many people, it transforms what’s possible. You stop translating. You stop explaining. You spend less time on the basics and more time actually getting to know someone.
            </p>
          </div>
        </section>

        {/* Why intentionality matters */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Why Intentional Dating Matters Across The Diaspora
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Mainstream dating apps were not designed for Black people. They were designed for a default user and then opened up to everyone else — which means too many of the standard experiences (algorithms, photo filters, fetishization patterns, harassment dynamics) are misaligned with how diaspora dating actually works at its healthiest.
            </p>
            <p>
              UnityLove takes the opposite approach. Black, poly-aware, family-friendly, and culturally fluent are the defaults — not the exceptions. That changes what kind of conversations show up in your inbox.
            </p>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Continue Reading
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/black-sister-wife-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Sister Wife Dating</p>
              <p className="text-sm text-slate-600 mt-1">A community for Black plural family-building.</p>
            </a>
            <a href="/poly-dating-for-couples" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Dating For Couples</p>
              <p className="text-sm text-slate-600 mt-1">For couples exploring ENM together.</p>
            </a>
            <a href="/how-to-find-a-sister-wife" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">How To Find A Sister Wife</p>
              <p className="text-sm text-slate-600 mt-1">Practical, respectful guidance.</p>
            </a>
            <a href="/ethical-polyamory-for-beginners" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Ethical Polyamory For Beginners</p>
              <p className="text-sm text-slate-600 mt-1">Communication, boundaries, and care.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
